import { WearWithAiListingMediaTypeEnumItem } from '@ps/api/types';
import { ListingType } from '@ps/models';

/**
 * Predicate matching the legacy Wear-With-AI gate used everywhere
 * (`ProductActionsMenu.tsx:194`, `ListingImageCarouselAffiliateOverlay.tsx:205`,
 * `ProductList.tsx:425`, `AllProductsGrid.tsx:204`):
 *
 *   type === Affiliate && isEligibleForWearMe === true
 *
 * Used to gate every Wear-With-AI surface — the carousel merge,
 * the card-thumbnail override, the menu item, the PDP CTA, etc.
 */
export function isWearWithAiSuitable(listing: {
  type?: string;
  isEligibleForWearMe?: boolean;
}): boolean {
  return (
    listing.type === ListingType.Affiliate &&
    listing.isEligibleForWearMe === true
  );
}

/**
 * Resolve the card thumbnail URL for a listing.
 *
 * Mirrors legacy `ProductList.tsx:474-477`:
 *   - If the listing is Wear-With-AI suitable AND has a curated
 *     `defaultWearWithAiMedia` with `type === Image`, use that
 *     media's URL (the creator's chosen showcase image).
 *   - Otherwise fall back to `thumbnailImage ?? defaultImage`.
 *
 * Videos are skipped — the card slot is a static image. The
 * type gate is defensive: legacy doesn't explicitly check
 * `isAffiliate && isEligibleForWearMe` here, but in practice
 * the backend only populates `defaultWearWithAiMedia` for that
 * cohort, so layering the gate makes the contract explicit and
 * keeps misconfigured listings from surfacing stale AI media.
 */
export function getListingCardImage(listing: {
  type?: string;
  isEligibleForWearMe?: boolean;
  defaultImage?: string;
  thumbnailImage?: string;
  defaultWearWithAiMedia?: {
    type?: string;
    url?: string;
  };
}): string | undefined {
  if (
    isWearWithAiSuitable(listing) &&
    listing.defaultWearWithAiMedia?.type ===
      WearWithAiListingMediaTypeEnumItem.Image &&
    listing.defaultWearWithAiMedia.url
  ) {
    return listing.defaultWearWithAiMedia.url;
  }
  return listing.thumbnailImage ?? listing.defaultImage;
}
